import { memo, type ReactNode } from 'react'

import { TableBody, TableCell, TableRow } from '@/components/ui/table'

import type { DataTableAction, DataTableColumn } from './data-table.interface'
import { DataTableRow } from './data-table-row.molecule'

interface DataTableBodyProps<TData> {
  data: TData[]
  columnList: DataTableColumn<TData>[]
  getRowId: (row: TData) => string
  isSelectable: boolean
  isRowSelected: (rowId: string) => boolean
  onSelectRow: (rowId: string, isChecked: boolean) => void
  actionList: DataTableAction<TData>[]
  hasActions: boolean
  totalColSpan: number
  emptyMessage: string
}

/**
 * Renders the data table body with rows or an empty state message.
 */
const DataTableBodyInner = <TData,>({
  data,
  columnList,
  getRowId,
  isSelectable,
  isRowSelected,
  onSelectRow,
  actionList,
  hasActions,
  totalColSpan,
  emptyMessage,
}: DataTableBodyProps<TData>): ReactNode => (
  <TableBody>
    {data.length === 0 ? (
      <TableRow>
        <TableCell colSpan={totalColSpan} className="h-24 text-center text-muted-foreground">
          {emptyMessage}
        </TableCell>
      </TableRow>
    ) : (
      data.map((row) => {
        const rowId = getRowId(row)
        return (
          <DataTableRow
            key={rowId}
            row={row}
            rowId={rowId}
            columnList={columnList}
            isSelectable={isSelectable}
            isSelected={isRowSelected(rowId)}
            onSelectChange={onSelectRow}
            actionList={actionList}
            hasActions={hasActions}
          />
        )
      })
    )}
  </TableBody>
)

export const DataTableBody = memo(DataTableBodyInner) as typeof DataTableBodyInner
